import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import Barcode from "react-barcode";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

import styles from "./DynamicBarcode.module.sass";
import { useLocation } from "react-router-dom";
import moment from "moment";
const BarcodePrint = () => {
  const location = useLocation();
  const {
    products,
    cols = 1,
    rows = 1,
    units = "mm",
    width = 100,
    height = 25,
    above_barcode,
    below_barcode,
    barcode_symbol = "SKU",
  } = location.state || {};

  const [loading, setLoading] = useState(false);
  const componentRef = useRef();
  const chunkedBarcodesRef = useRef([]);
  const isDownloaded = useRef(false);

  const expandProducts = () => {
    return products.flatMap((product) =>
      Array.from({ length: product.barcodeQuantity || 1 }, () => ({
        ...product,
      }))
    );
  };

  const expandedProducts = products ? expandProducts() : [];
  const barcodesPerPage = cols * rows;

  const createBarcodePages = () => {
    return Array.from(
      { length: Math.ceil(expandedProducts.length / barcodesPerPage) },
      (_, i) =>
        expandedProducts.slice(i * barcodesPerPage, (i + 1) * barcodesPerPage)
    ).map((pageProducts) =>
      Array.from({ length: Math.ceil(pageProducts.length / cols) }, (_, j) =>
        pageProducts.slice(j * cols, (j + 1) * cols)
      )
    );
  };

  const barcodePages = expandedProducts.length > 0 ? createBarcodePages() : [];

  const convertHtmlToImage = async (elementRefs) => {
    const images = [];
    for (let i = 0; i < elementRefs.length; i++) {
      if (elementRefs[i]) {
        // Use higher scale for better quality
        const canvas = await html2canvas(elementRefs[i], {
          scale: 3,
          logging: false,
          useCORS: true,
          allowTaint: true,
        });
        images.push(canvas.toDataURL("image/png"));
      }
    }
    return images;
  };

  const handlePrintPDF = async () => {
    if (isDownloaded.current || !barcodePages.length) return;
    isDownloaded.current = true;
    setLoading(true);

    try {
      const parsedWidth = parseFloat(width) || 100;
      const parsedHeight = parseFloat(height) || 25;

      // Create PDF with proper dimensions
      const pdf = new jsPDF({
        orientation: parsedWidth > parsedHeight ? "landscape" : "portrait",
        unit: units,
        format: [parsedWidth, parsedHeight],
      });

      const images = await convertHtmlToImage(chunkedBarcodesRef.current);
      images.forEach((image, index) => {
        if (index > 0) {
          pdf.addPage();
        }
        pdf.addImage(image, "PNG", 0, 0, parsedWidth, parsedHeight);
      });

      pdf.save(`barcodes_${new Date().getTime()}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setLoading(false);
      // Reset download flag after a delay to allow regeneration if needed
      setTimeout(() => {
        isDownloaded.current = false;
      }, 500);
    }
  };

  useLayoutEffect(() => {
    if (
      barcodePages.length > 0 &&
      chunkedBarcodesRef.current.length === barcodePages.length &&
      !isDownloaded.current
    ) {
      handlePrintPDF();
    }
  }, [barcodePages]);

  // Reset refs when products change
  useEffect(() => {
    chunkedBarcodesRef.current = [];
    isDownloaded.current = false;
  }, [products]);

  // Generate styles based on units
  const getPageStyle = () => {
    const parsedWidth = parseFloat(width) || 100;
    const parsedHeight = parseFloat(height) || 25;

    return {
      width: `${parsedWidth}${units}`,
      height: `${parsedHeight}${units}`,
      margin: "0",
      padding: "0",
      boxSizing: "border-box",
      position: "relative",
      overflow: "hidden",
    };
  };

  const getRowStyle = () => {
    const parsedWidth = parseFloat(width) || 100;
    const parsedHeight = parseFloat(height) || 25;
    const rowHeight = parsedHeight / rows;

    return {
      width: `${parsedWidth}${units}`,
      height: `${rowHeight}${units}`,
      margin: "0",
      padding: "0",
      boxSizing: "border-box",
    };
  };

  const getStickerStyle = () => {
    const parsedWidth = parseFloat(width) || 100;
    const parsedHeight = parseFloat(height) || 25;
    const stickerWidth = parsedWidth / cols;
    const rowHeight = parsedHeight / rows;

    return {
      width: `${stickerWidth}${units}`,
      height: `${rowHeight}${units}`,
      margin: "0",
      padding: `${rowHeight * 0.05}${units}`,
      boxSizing: "border-box",
    };
  };
  const getCurrentDate = () => {
    return moment().format("DD/MM/YYYY");
  };
  const processText = (text) => {
    if (text === "CURRENT DATE") {
      return getCurrentDate();
    }
    return text;
  };
  const getBarcodeStyle = () => {
    // Scale barcode to fit within sticker
    const parsedWidth = parseFloat(width) || 100;
    const parsedHeight = parseFloat(height) || 25;
    const stickerWidth = parsedWidth / cols;
    const rowHeight = parsedHeight / rows;

    // Leave space for text above and below
    const barcodeWidth = stickerWidth * 0.9;
    const barcodeHeight = rowHeight * 0.6;

    return {
      width: barcodeWidth,
      height: barcodeHeight,
    };
  };

  if (!products || expandedProducts.length === 0) {
    return <p>No barcodes to print</p>;
  }

  return (
    <div>
      {loading && <h1>Generating PDF...</h1>}
      <div className={styles.labelsContainer} ref={componentRef}>
        {barcodePages.map((page, pageIndex) => (
          <div
            className={styles.page}
            key={`page-${pageIndex}`}
            ref={(el) => (chunkedBarcodesRef.current[pageIndex] = el)}
            style={getPageStyle()}
          >
            {page.map((rowProducts, rowIndex) => (
              <div
                className={styles.row}
                key={`row-${pageIndex}-${rowIndex}`}
                style={getRowStyle()}
              >
                {rowProducts.map((product, colIndex) => {
                  const barcodeValue =
                    barcode_symbol === "SKU"
                      ? product.sku
                      : product[barcode_symbol] || product.barcode || "";
                  const aboveText = above_barcode
                    ? product[above_barcode] === "CURRENT_DATE"
                      ? getCurrentDate()
                      : product[above_barcode] || ""
                    : "";

                  // Get below barcode text, replacing CURRENT DATE if needed
                  const belowText = below_barcode
                    ? product[below_barcode] === "CURRENT_DATE"
                      ? getCurrentDate()
                      : product[below_barcode] || ""
                    : "";
                  return (
                    <div
                      className={styles.sticker}
                      key={`product-${
                        product.id || colIndex
                      }-${pageIndex}-${rowIndex}-${colIndex}`}
                      style={getStickerStyle()}
                    >
                      {above_barcode && (
                        <div className={styles.aboveBarcode}>{aboveText}</div>
                      )}
                      <div className={styles.barcodeInner}>
                        <Barcode
                          value={barcodeValue || "0000000000"}
                          width={1}
                          height={50}
                          fontSize={10}
                          displayValue={false}
                          margin={0}
                        />
                        <div className={styles.barcodeValue}>
                          {barcodeValue || ""}
                        </div>
                      </div>
                      {below_barcode && (
                        <div className={styles.belowBarcode}>{belowText}</div>
                      )}
                    </div>
                  );
                })}
                {rowProducts.length < cols &&
                  Array.from({ length: cols - rowProducts.length }).map(
                    (_, i) => (
                      <div
                        className={styles.sticker}
                        key={`empty-${pageIndex}-${rowIndex}-${i}`}
                        style={getStickerStyle()}
                      />
                    )
                  )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BarcodePrint;
